/**
 * Derive PeopleProfileCta primary/secondary actions from auth + profile.
 */

export type PeopleProfileCtaSurface = 'hub' | 'profile' | 'zero_results';

export type PeopleProfileCtaProfileSnapshot = {
  slug: string;
  verification_status: string;
  is_public: boolean;
  publicly_visible?: boolean;
};

export type PeopleProfileCtaPrimaryKind =
  | 'create'
  | 'finish'
  | 'view'
  | 'none';

export type PeopleProfileCtaDerived = {
  primary: PeopleProfileCtaPrimaryKind;
  showSoftManage: boolean;
  managePath: string;
  viewPath: string | null;
};

const SETTINGS_PROFILE = '/dashboard/settings?tab=profile';

export function derivePeopleProfileCtaState(input: {
  isAuthenticated: boolean;
  profile: PeopleProfileCtaProfileSnapshot | null;
  /** When on /people/:slug, the slug being viewed (to hide create on own page). */
  viewingSlug?: string | null;
}): PeopleProfileCtaDerived {
  const managePath = SETTINGS_PROFILE;
  if (!input.isAuthenticated) {
    return {
      primary: 'create',
      showSoftManage: false,
      managePath,
      viewPath: null,
    };
  }

  const profile = input.profile;
  if (!profile) {
    return {
      primary: 'create',
      showSoftManage: false,
      managePath,
      viewPath: null,
    };
  }

  const viewPath = `/people/${encodeURIComponent(profile.slug)}`;
  const viewingOwn =
    Boolean(input.viewingSlug) &&
    input.viewingSlug!.trim().toLowerCase() === profile.slug.trim().toLowerCase();

  const verifiedPublic =
    profile.verification_status === 'verified' && profile.is_public === true;

  if (viewingOwn) {
    return {
      primary: 'none',
      showSoftManage: true,
      managePath,
      viewPath,
    };
  }

  if (verifiedPublic) {
    return {
      primary: 'view',
      showSoftManage: true,
      managePath,
      viewPath,
    };
  }

  return {
    primary: 'finish',
    showSoftManage: true,
    managePath,
    viewPath,
  };
}

export function buildPeopleProfileFinishPath(currentPath: string): string {
  const base = currentPath.split('?')[0] || '/people';
  const params = new URLSearchParams();
  params.set('people_profile', 'finish');
  return `${base}?${params.toString()}`;
}
